import React from 'react';
import styled from '@emotion/styled';
import { EmotionBreakpoint } from 'dw-us-ui';
import { useCaseBuilderContext } from '../../context/CaseBuilderContext';

const WineDetailsWrapper = styled.div`
	width: 100%;
	position: relative;

	${EmotionBreakpoint('mobile')} {
		order: 1;
		overflow: hidden;
	}
	${EmotionBreakpoint('tablet')} {
		order: 1;
		overflow: hidden;
	}
`;

const WineDetailsCarousel = styled.div`
	display: flex;
	transition: transform 0.3s ease-out;
	transform: translateX(${(props) => props.offset}px);

	@media (min-width: 1025px) {
		position: relative;
	}

	${EmotionBreakpoint('mobile')} {
		transition: transform 0.3s ease-out;
		transform: translateX(${(props) => props.offset}px);
	}
	${EmotionBreakpoint('tablet')} {
		transition: transform 0.3s ease-out;
		transform: translateX(${(props) => props.offset}px);
	}
`;

const WineDetailsContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 1384px;
	flex: 0 0 100%;
	min-width: 100%;

	@media (min-width: 1025px) {
		display: ${(props) => (props.isActive ? 'flex' : 'none')};
	}

	${EmotionBreakpoint('mobile')} {
		min-width: 100%;
		display: flex;
	}
	${EmotionBreakpoint('tablet')} {
		min-width: 100%;
		display: flex;
	}
`;

const WineNameSection = styled.div`
	display: flex;
	gap: 10px;
	align-items: flex-start;
	padding: 24px;
	border-bottom: 1px solid #b1927b;
`;

const WineName = styled.h2`
	font-family: 'Oswald', sans-serif;
	font-weight: 500;
	font-size: 34px;
	line-height: 1.05;
	letter-spacing: -1.02px;
	text-transform: uppercase;
	color: #222222;
	margin: 0;
	flex: 1;
	text-align: left;

	.fox & {
		font-family: 'Roboto', sans-serif;
		font-weight: 600;
		font-size: 22px;
		text-transform: none;
		letter-spacing: -0.22px;
	}
	.wsj & {
		font-family: 'Escrow Banner Compressed', serif;
		font-weight: 700;
	}
	.npr & {
		font-family: 'Gotham', sans-serif;
		font-weight: 600;
		font-size: 24px;
	}
	.osw & {
		font-family: 'Neue Aachen Pro', sans-serif;
		font-weight: 500;
		font-size: 24px;
	}

	${EmotionBreakpoint('mobile')} {
		font-size: 28px;
	}
`;

const ContentSection = styled.div`
	display: flex;
	gap: 24px;
	width: 100%;

	${EmotionBreakpoint('mobile')} {
		flex-direction: column;
	}
	${EmotionBreakpoint('tablet')} {
		flex-direction: column;
	}
`;

const WineDetailsSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px;
`;

const SectionTitle = styled.h3`
	font-family: 'Roboto', sans-serif;
	font-weight: 500;
	font-size: 24px;
	line-height: 1.05;
	color: #222222;
	margin: 0;
	text-align: left;
	.wsj & {
		font-family: Arial, sans-serif;
		font-weight: 700;
	}
`;

const DetailsList = styled.div`
	display: flex;
	gap: 64px;

	${EmotionBreakpoint('mobile')} {
		flex-direction: column;
		gap: 24px;
	}
`;

const DetailsColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const DetailItem = styled.div`
	display: flex;
	gap: 16px;
	align-items: center;
`;

const DetailText = styled.p`
	font-family: 'Roboto', sans-serif;
	font-weight: 400;
	font-size: 16px;
	line-height: 1.4;
	color: #222222;
	margin: 0;
	.wsj & {
		font-family: Arial, sans-serif;
	}
`;

const CountryFlag = styled.div`
	width: 30px;
	height: 21px;
	border-radius: 2px;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

const FlavorProfileSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px;
`;

const FlavorList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`;

const FlavorItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const FlavorTitle = styled.h4`
	font-family: 'Roboto', sans-serif;
	font-weight: 700;
	font-size: 16px;
	line-height: 1.4;
	color: #222222;
	margin: 0;
	letter-spacing: 0.16px;
	text-align: left;
	.wsj & {
		font-family: Arial, sans-serif;
	}
`;

const FlavorDescription = styled.p`
	font-family: 'Roboto', sans-serif;
	font-weight: 400;
	font-size: 16px;
	line-height: 1.4;
	color: #222222;
	margin: 0;
	text-align: left;
	.wsj & {
		font-family: Arial, sans-serif;
	}
`;

const WineDetails = () => {
	const {
		wineSlots,
		activeBottleIndex,
		carouselOffset,
		detailSlideRefs,
		touchHandlers,
	} = useCaseBuilderContext();

	return (
		<WineDetailsWrapper {...touchHandlers}>
			<WineDetailsCarousel offset={carouselOffset}>
				{wineSlots.slice(0, 12).map((wineData, index) => (
					<WineDetailsContainer
						key={`detail-${index}`}
						ref={(el) => (detailSlideRefs.current[index] = el)}
						isActive={index === activeBottleIndex}
					>
						{wineData?.product && (
							<>
								<WineNameSection>
									<WineName>
										{wineData.product.name} {wineData.product.vintage}
									</WineName>
								</WineNameSection>

								<ContentSection>
									<WineDetailsSection>
										<SectionTitle>Wine Details</SectionTitle>
										<DetailsList>
											<DetailsColumn>
												<DetailItem>
													<img
														src='/images/us/common/icons/wineGlassIcon.svg'
														alt='Wine Style'
													/>
													<DetailText>
														{wineData.product.styleName || 'Wine Style'}
													</DetailText>
												</DetailItem>
												<DetailItem>
													<img
														src='/images/us/common/icons/grapeIcon.svg'
														alt='Grape Varietal'
													/>
													<DetailText>
														{wineData.product.grapeName || 'Grape Varietal'}
													</DetailText>
												</DetailItem>
												<DetailItem>
													<CountryFlag>
														<img
															src={`/images/us/en/common/icons/flags/${
																wineData.product.countryName || 'default'
															}.svg`}
															alt={wineData.product.countryName || 'Country'}
															onError={(e) => {
																e.currentTarget.src =
																	'/images/us/en/common/icons/flags/default.svg';
															}}
														/>
													</CountryFlag>
													<DetailText>
														{wineData.product.countryName || 'Country'}
													</DetailText>
												</DetailItem>
											</DetailsColumn>

											<DetailsColumn>
												<DetailItem>
													<img
														src='/images/us/common/icons/shotsIcon.svg'
														alt='Shot Glass Icon'
													/>
													<DetailText>
														{wineData.product.alcoholPercent || '14.5'}% ABV
													</DetailText>
												</DetailItem>
												<DetailItem>
													<img
														src='/images/us/common/icons/wineBottleIcon.svg'
														alt='Wine Bottle'
													/>
													<DetailText>
														{wineData.product.skus?.[0]?.bottleVolume || '750'}{' '}
														ml
													</DetailText>
												</DetailItem>
											</DetailsColumn>
										</DetailsList>
									</WineDetailsSection>

									<FlavorProfileSection>
										<SectionTitle>Flavor Profile</SectionTitle>
										<FlavorList>
											<FlavorItem>
												<FlavorTitle>Wine Flavor Profile</FlavorTitle>
												<FlavorDescription>
													{wineData.product.tastingNotes
														?.shortTastingNotesTaste ||
														wineData.product.description ||
														'Wine flavor profile details will appear here.'}
												</FlavorDescription>
											</FlavorItem>
											<FlavorItem>
												<FlavorTitle>Wine Food Pairings</FlavorTitle>
												<FlavorDescription>
													{wineData.product.tastingNotes
														?.shortTastingNotesAroma ||
														wineData.product.foodPairings ||
														'Food pairing suggestions will appear here.'}
												</FlavorDescription>
											</FlavorItem>
										</FlavorList>
									</FlavorProfileSection>
								</ContentSection>
							</>
						)}
					</WineDetailsContainer>
				))}
			</WineDetailsCarousel>
		</WineDetailsWrapper>
	);
};

export default WineDetails;
